<template>
  <section>
    <!-- Blocks the page while the file is uploading, so a second drop can't start a competing import. -->
    <z-overlay v-if="isUploading" notice="uploading" custom-style="position: fixed;" @cancel="cancelUpload" />
    <div class="row mt-2">
      <div class="col">
        <p>
          Use this facility to import the category/vehicle matrix <b>KEYWORD</b> data, including setting up <b>NEW</b> category/vehicle matrix items.
          To kick off the import, drag and drop your comma-delimited file onto the grey area below.
        </p>
        <p><u>REMEMBER:</u> this only imports <b>KEYWORDS, CALIBRATION, and TRIGGER NOTICES</b>.</p>
        <p>
          The file should have the following columns populated: Adas Category, Make, Model, Year Start, Year End, System, Location, Location Keywords,
          Labor Keywords, Parts Keywords, Calibration, Trigger Notice, Model Exclusions (in this order).
          <a href="https://opusadas.blob.core.windows.net/samples/ADAS%20Matrix%20Keyword%20SAMPLE%20Import.csv">Sample File Here</a>
        </p>
        <p>NOTE: any Location Keywords having commas (e.g., PILLARS, ROCKER & FLOOR), delimit them with a pipe (e.g., PILLARS| ROCKER & FLOOR). Additionally, specifying Calibration Sub Type requires the correct specification of the "OR" type as the Calibration Type</p>
        
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-3">
        <div class="row">
          <div class="col">
            <label for="columns">Columns for Import or Export:</label>
          </div>
        </div>
        <div class="row mt-1">
          <div class="col">
            <Multiselect
              ref="columns"
              v-model="columns"
              class="form-control custom-multiselect"
              placeholder="choose columns(s) to import or export"
              :open-direction="'top'"
              :disabled="isBusy"
              :close-on-select="false"
              :options="availableColumns"
              mode="multiple"
              label="name"
              value-prop="id"
              @deselect="selectedColumns"
              @clear="selectedColumns"
              @select="selectedColumns"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <ul class="custom-list">
              <li v-for="column in chosenColumns" :key="column.id">
                <span>{{ column.name }}</span>
                <a v-if="column.isRemovable && !isBusy" @click="removeColumn(column.id)">
                  <i class="fas fa-times custom-trash"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="col custom-uploader">
        <div class="row">
          <div class="col">
            <file-pond
              ref="pond"
              name="test"
              :server="{ process, revert, restore, load, fetch }"
              :label-idle="isBusy ? 'Import in progress...' : 'Drop *.CSV file here...'"
              allow-multiple="true"
              accepted-file-types="text/plain"
              :disabled="isBusy"
              :files="uploadFiles"
              @init="handleFilePondInit"
              @updatefiles="handleFilePondAdd"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <import-matrix-status ref="importStatus" @isComplete="complete" />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <button class="btn btn-primary btn-sm custom-export" :disabled="isDownloading || isBusy" @click="exportMatrixVehicleTriggers">
              <FontAwesomeIcon v-if="isDownloading" icon="fa-solid fa-spinner me-2" spin />
              Export Vehicle Matrix
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <span class="custom-message"><FontAwesomeIcon v-if="isImporting" icon="fa-solid fa-spinner me-2" spin />{{ message }}</span>
        <!-- Escape hatch: the progress feed can stall, and without this the form stays disabled until a reload. -->
        <button v-if="isImporting" class="btn btn-link btn-sm custom-unlock" @click="unlock">not responding? re-enable this form</button>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { adasmatrixStore } from '@/stores/adasmatrix'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Multiselect from '@vueform/multiselect'
import ImportMatrixStatus from './matrix-import-status.vue'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import axios from 'axios'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.css'
const FilePond = vueFilePond()
export default {
  name: 'MatrixImportKeywords',
  components: {
    ImportMatrixStatus,
    ZOverlay,
    FilePond,
    FontAwesomeIcon,
    Multiselect
  },
  // Let the host lock its tab strip while this screen is busy.
  emits: ['busy'],
  watch: {
    isBusy(value) {
      this.$emit('busy', value)
    }
  },
  data() {
    return {
      firm: 1,
      isImporting: false,
      isUploading: false,
      uploadSource: null,
      uploadFiles: [],
      apiUrl: '',
      message: null,
      isDownloading: false,
      columns: [0, 1, 3, 4, 5, 6],
      availableColumns: [
        {
          id: 0,
          name: 'AdasCategory',
          isRemovable: false
        },
        {
          id: 1,
          name: 'Make',
          isRemovable: false
        },
        {
          id: 2,
          name: 'Model',
          isRemovable: true
        },
        {
          id: 3,
          name: 'YearStart',
          isRemovable: false
        },
        {
          id: 4,
          name: 'YearEnd',
          isRemovable: false
        },
        {
          id: 5,
          name: 'System',
          isRemovable: false
        },
        {
          id: 6,
          name: 'Location',
          isRemovable: false
        },
        {
          id: 7,
          name: 'LocationKeywords',
          isRemovable: true
        },
        {
          id: 8,
          name: 'LaborKeywords',
          isRemovable: true
        },
        {
          id: 9,
          name: 'PartsKeywords',
          isRemovable: true
        },
        {
          id: 10,
          name: 'Calibration',
          isRemovable: true
        },
        {
          id: 11,
          name: 'TriggerNotice',
          isRemovable: true
        },
        {
          id: 12,
          name: 'ModelExclusions',
          isRemovable: true
        },
        {
          id: 13,
          name: 'CalibrationSubTypes',
          isRemovable: true
        },
        {
          id: 14,
          name: 'AddlOperationRequired',
          isRemovable: true
        },
        {
          id: 15,
          name: 'AddlOperations',
          isRemovable: true
        }
      ]
    }
  },
  mounted() {
    this.isImporting = false
    this.isUploading = false
    this.isDownloading = false
    this.apiUrl = window.config ? window.config.apiUrl : ''
  },
  computed: {
    ...mapState(authStore, ['token']),
    ...mapState(adasmatrixStore, ['matrixKeywordsTriggers']),
    // Uploading or importing: either way the form must not accept another submission.
    isBusy() {
      return this.isUploading || this.isImporting
    },
    chosenColumns() {
      let _columns = this.availableColumns.filter((column) => this.columns.includes(column.id))
      return _columns
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, ['importMakeMatrixKeywords', 'checklistMatrixVehicleKeywordsTriggersExport']),
    async importKeywords(data) {
      let path = data.data.path
      let filename = data.data.filename
      this.isImporting = true
      let columns = null
      if (this.chosenColumns.length > 0) {
        columns = this.chosenColumns.map((item) => item.name).join(',')
      }
      console.log('Uploaded File', {
        path: path,
        filename: filename,
        chosenColumns: columns
      })
      this.$refs.pond.removeFiles({ revert: true })
      this.message = 'upload complete, processing...'
      await this.importMakeMatrixKeywords({
        path: path,
        filename: filename,
        columns: columns
      })
    },
    complete() {
      this.isImporting = false
      this.message = 'import complete'
    },
    // Dismiss the upload overlay by cancelling the request behind it.
    cancelUpload() {
      if (this.uploadSource) {
        this.uploadSource.cancel('Operation canceled by the user.')
      }
      this.isUploading = false
      this.message = 'upload cancelled'
    },
    // Release the form when the progress feed has stopped reporting.
    unlock() {
      this.isImporting = false
      this.message = 'form re-enabled - the import may still be running on the server'
    },
    process(fieldName, file, metadata, load, error, progress) {
      let $this = this
      this.message = null
      this.isUploading = true
      // set data
      const formData = new FormData()
      formData.append('files', file, file.name)
      formData.append('firm', this.firm)
      formData.append('preference', 'MakeMatrixKeywords')
      formData.append('path', `adasmatrix/${this.firm}`)
      // related to aborting the request
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      this.uploadSource = source
      axios.interceptors.request.use(
        (config) => {
          config.headers['Authorization'] = 'Bearer ' + this.token
          return config
        },
        (error) => {
          return Promise.reject(error)
        }
      )
      // the request itself
      axios({
        method: 'post',
        url: `${this.apiUrl}/data/uploadFile`,
        data: formData,
        cancelToken: source.token,
        onUploadProgress: (e) => {
          // updating progress indicator
          progress(e.lengthComputable, e.loaded, e.total)
        }
      })
        .then((response) => {
          // Hand over from the blocking overlay to the disabled-form state.
          $this.isUploading = false
          $this.importKeywords(response)
        })
        .catch((thrown) => {
          // Always clear the overlay, or a failed upload leaves the page blocked.
          $this.isUploading = false
          if (axios.isCancel(thrown)) {
            console.log('Request canceled', thrown.message)
          } else {
            console.log('Upload failed', thrown)
            $this.message = 'upload failed, nothing was imported - please try again'
          }
        })
      // Setup abort interface
      return {
        abort: () => {
          source.cancel('Operation canceled by the user.')
          $this.isUploading = false
        }
      }
    },
    handleError(error) {
      console.log('Error', error)
    },
    handleFilePondInit: function () {},
    handleFilePondAdd: function () {
      this.$refs.importStatus.reset()
    },
    selectedColumns() {
      console.log('Selected Columns', {
        columns: this.columns,
        chosenColumns: this.chosenColumns
      })
    },
    removeColumn(id) {
      if (this.isBusy) return
      console.log('Removing Column', id)
      this.columns = this.columns.filter((column) => column !== id)
      console.log('Columns', this.columns)
    },
    decodeHtml(html) {
      var txt = document.createElement('textarea')
      txt.style.display = 'none' // Ensure it's not visible
      document.body.appendChild(txt) // Temporarily add it to the DOM
      txt.innerHTML = html
      var decodedHtml = txt.value
      document.body.removeChild(txt) // Clean up by removing the element
      return decodedHtml
    },
    async exportMatrixVehicleTriggers() {
      this.isDownloading = true
      let columns = null
      if (this.chosenColumns.length > 0) {        
        columns = this.chosenColumns.map((item) => item.name).join(',')
      }
      console.log('Exporting Matrix Vehicle Triggers', columns)
      await this.checklistMatrixVehicleKeywordsTriggersExport(columns)
      if (this.matrixKeywordsTriggers) {
        console.log('Matrix Keywords Triggers', this.matrixKeywordsTriggers)
        let keywordTriggers = this.decodeHtml(this.matrixKeywordsTriggers)
        console.log('Clean Matrix Keywords Triggers', keywordTriggers)
        var blob = new Blob([this.matrixKeywordsTriggers], { type: 'text/csv' })
        var link = document.createElement('a')
        link.style.display = 'none'
        var now = moment().format('MMDDYYYY')
        if (window.URL.createObjectURL) {
          link.href = window.URL.createObjectURL(blob)
          link.download = `ADAS MAP Vehicle Matrix All Makes Models with Trigger Notice ${now}.csv`
          link.click()
        } else {
          this.message = 'Refresh your browser to generate this download.'
        }
      }
      this.isDownloading = false
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-message {
  margin-left: 15px;
  margin-right: 15px;
  font-size: 0.9em;
}
.custom-uploader {
  padding-top: 25px;
}
.custom-list {
  margin-top: 10px;
  list-style-type: circle;
  padding-left: 45px;
}
.custom-trash {
  float: right;
  margin-right: 10px;
  color: rgb(151, 0, 0);
  cursor: pointer;
}
.custom-export {
  margin-top: 40px;
}
.custom-unlock {
  font-size: 0.8em;
  padding: 0;
  vertical-align: baseline;
}
</style>
